import React, { FC, useCallback, useState } from 'react';
import { Button, Card, Grid, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
	Configuration,
	CONFIGURATION,
	PostConfiguration,
} from 'data/configuration';
import { Gutter } from 'components/Gutter';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import { AgentStates } from 'types';
import { startAgent, stopAgent } from 'apis';
import { useAccount } from 'wagmi';
import { toast } from 'react-toastify';

const TwoColumnGrid = styled('div')(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '2.1fr .8fr',
	gap: theme.spacing(2),
	[theme.breakpoints.down(1200)]: {
		gridTemplateColumns: '1fr',
	},
}));

const Row = styled('div')`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
`;

const SignalCard = styled(Card)(({ theme }) => ({
	padding: theme.spacing(5),
	backdropFilter: 'blur(14px)',
	background: '#ffffff55',
	marginTop: 20,
	boxShadow: 'none',
	border: 'none',
	height: '100%',
	borderRadius: 20,
	position: 'relative',
}));

const StyledTitle = styled(Typography)`
	font-weight: 600;
`;

const Text = styled(Typography)`
	position: absolute;
	bottom: 10px;
	right: 10px;
`;

const StyledButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: 'contained' | 'outlined' }>(({ theme, variant }) => ({
	...(variant === 'contained' && {
		backgroundColor: '#2dbcbb',
		boxShadow: 'none',
		borderRadius: 50,
		color: '#fff',
		'&:hover': {
			backgroundColor: '#1e807f',
			boxShadow: 'none',
		},
		'&:disabled': {
			backgroundColor: '#2b3a4155',
			boxShadow: 'none',
			color: '#fff',
		},
	}),
	...(variant === 'outlined' && {
		backgroundColor: 'transparent',
		color: '#36454F',
		border: '1px solid #36454F',
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: 'rgba(54, 69, 79, 0.08)',
		},
		'&:disabled': {
			backgroundColor: '#2b3a4155',
			boxShadow: 'none',
			color: '#fff',
		},
	}),
}));

interface ReadOnlyAgentProps {
	initialConfig?: Configuration;
	onSuccess?(): void;
	selectedAgent?: Omit<PostConfiguration, 'agentKey' | 'agentAddress'>;
}

const ReadOnlyAgent: FC<ReadOnlyAgentProps> = ({
	initialConfig = CONFIGURATION,
	onSuccess,
	selectedAgent,
}) => {
	const [agent, setAgent] = useState(selectedAgent);
	const { address } = useAccount();

	const startSelectedAgent = useCallback(async (): Promise<void> => {
		try {
			if (agent?.status === 'created' || agent?.status === 'stopped') {
				const res = await startAgent(agent?.id || '', address || '');

				setAgent(res as any);
			} else {
				const res = await stopAgent(agent?.id || '', address || '');

				setAgent(res as any);
			}
			onSuccess?.();
			const message: any = {
				created: 'Successfully started',
				started: 'Successfully requested for agent stop',
				stopped: 'Successfully started',
			};
			toast(message[agent?.status || 'created'], {
				type: 'success',
			});
		} catch (e) {
			//
		}
	}, [address, agent]);

	return (
		<>
			<TwoColumnGrid>
				<Row>
					<IconButton
						onClick={() => history.back()}
						aria-label="back"
					>
						<ArrowBackIcon />
					</IconButton>
					<StyledTitle variant="h5" gutterBottom fontWeight={600}>
						Agent Configuration
					</StyledTitle>
					<StyledTitle variant="h5" gutterBottom fontWeight={600} />
				</Row>
			</TwoColumnGrid>

			<TwoColumnGrid>
				<Grid container justifyContent="center">
					<Grid item xs={12} sm={8} md={6}>
						<SignalCard>
							<div>
								<Typography variant="subtitle1" gutterBottom>
									Total Trade Amount:{' '}
									{initialConfig.total_trade_amount}
								</Typography>
								<Typography variant="subtitle1" gutterBottom>
									Max Risk Percentage:{' '}
									{initialConfig.max_amount_per_trade}%
								</Typography>
								<Typography variant="subtitle1" gutterBottom>
									Take Profit Per Trade:{' '}
									{initialConfig.take_profit_per_trade}%
								</Typography>
								<Typography variant="subtitle1" gutterBottom>
									Stop Loss per trade:{' '}
									{initialConfig.stop_loss_per_trade}%
								</Typography>
								<Typography variant="subtitle1" gutterBottom>
									Status:{' '}
									{agent?.status
										?.toUpperCase()
										.replaceAll('-', ' ')}
								</Typography>
								{agent?.status ===
									AgentStates['stop-requested'] && (
									<Typography
										variant="subtitle1"
										gutterBottom
									>
										Please wait for the agent to be stopped
									</Typography>
								)}
							</div>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'space-between',
								}}
							>
								<div>
									{agent?.status !==
										AgentStates['stop-requested'] && (
										<StyledButton
											variant="contained"
											onClick={() => startSelectedAgent()}
											size={'large'}
										>
											{agent?.status ===
												AgentStates.created &&
												'Start Agent'}
											{agent?.status ===
												AgentStates.stopped &&
												'Start Agent '}
											{agent?.status ===
												AgentStates.started &&
												'Stop Agent'}
										</StyledButton>
									)}
								</div>
								<Text
									variant="body2"
									gutterBottom
									color={'#666'}
								>
									Created At:{' '}
									{moment(agent?.createdAt).format(
										'DD-MMM-YYYY',
									)}
								</Text>
							</div>
						</SignalCard>
					</Grid>
				</Grid>
			</TwoColumnGrid>
			<Gutter />
			<Gutter spacing={2} />
		</>
	);
};

export default ReadOnlyAgent;
